import { CustomModal } from 'components/uikit/modal/CustomModal';
import React, { useCallback, useEffect, useState } from 'react';
import { QueryFactory } from 'services/api';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { Field } from 'components/uikit/Field';

import styles from '../projectImportFromSharePointModal/ProjectImportFromSharePointModal.module.scss';
import { Loading } from 'components/uikit/suspense/Loading';
import { useQueryClient } from '@tanstack/react-query';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import { Input } from 'components/uikit/inputs/Input';
import { FormError } from 'components/uikit/FormError';
import { requiredRule } from 'helpers/form/react-hook-form-helper';

export type ProjectImportFromSharePointModalProps = {
  onClose: () => void;
  isOpen: boolean;
  currentRevisionId: number;
};

export const ProjectImportFromSharePointModal: React.FC<
  ProjectImportFromSharePointModalProps
> = (props) => {
  const i18n = useScopedTranslation(
    'Page.Projects.ProjectDetailsPage.FoldersTree',
  );
  const buttonTitle = useScopedTranslation('Button.Title');
  const [isLoading, setIsLoading] = useState(false);
  const [unhandledError, setUnhandledError] = useState<string | undefined>();
  const queryClient = useQueryClient();

  const onModalClose = () => {
    form.reset();
    form.clearErrors();
    props.onClose();
  };

  const form = useAdvancedForm<{ sharepointUrl: string }>(
    useCallback(
      async (data) => {
        try {
          setIsLoading(true);
          await QueryFactory.RevisionClient.importFromSharepoint(
            props.currentRevisionId,
            data.sharepointUrl,
          );
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.RevisionQuery.searchQueryKey(),
          });
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.RevisionQuery.getQueryKey(
              props.currentRevisionId,
            ),
          });
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.DocumentQuery.searchQueryKey(),
          });

          onModalClose();
        } catch (e) {
          setExceptionErrors(e);
        } finally {
          setIsLoading(false);
        }
      },
      [props.currentRevisionId],
    ),
  );

  const setExceptionErrors = (e: any) => {
    form.clearErrors();

    if (e.errors) {
      form.setError('sharepointUrl', {
        type: 'custom',
        message: e.errors?.sharepointUrl,
      });
    }
    if (e.detail) {
      form.setError('sharepointUrl', {
        type: 'custom',
        message: e.detail,
      });
    }
    if (!e.errors && !e.detail) {
      setUnhandledError('Unexpected server error');
    }
  };

  useEffect(() => {
    if (props.isOpen) {
      setTimeout(() => {
        form.setFocus('sharepointUrl');
      });
    }
  }, [props.isOpen]);

  return (
    <CustomModal
      onClose={() => onModalClose()}
      isOpen={props.isOpen}
      isBlocking={true}
      title={i18n.t('import_revision_from_sharepoint_title')}
    >
      <Loading loading={isLoading}>
        <form onSubmit={form.handleSubmitDefault}>
          <Field title={i18n.t('sharepoint_url')} className={styles.field}>
            <Input
              {...form.register('sharepointUrl', { ...requiredRule() })}
              errorText={form.formState.errors.sharepointUrl?.message}
            />
          </Field>
          <FormError>{unhandledError}</FormError>
          <div className={styles.footer}>
            <Button
              className={styles.button}
              color={ButtonColor.Secondary}
              title={buttonTitle.t('cancel')}
              onClick={() => {
                onModalClose();
              }}
            />
            <Button
              testId="import"
              className={styles.button}
              color={ButtonColor.Default}
              type={'submit'}
              title={buttonTitle.t('import')}
            />
          </div>
        </form>
      </Loading>
    </CustomModal>
  );
};
